import { createPublicClient, createTestClient, createWalletClient } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { foundry } from "viem/chains";

import { PREFUNDED_ACCOUNTS } from "./accounts.js";
import { createTevmTransport } from "./tevm-transport.js";

import type {
  TevmConfig,
  WorkerErrorMessage,
  WorkerInitMessage,
  WorkerReadyMessage,
} from "./types.js";
import type { Chain, PublicClient, TestClient, WalletClient } from "viem";

/** Clients returned by createTevmClients(). */
export interface TevmClients {
  publicClient: PublicClient;
  walletClient: WalletClient;
  testClient: TestClient;
  worker: Worker;
  chain: Chain;
}

/** Build a chain definition for the given config. */
function buildChain(config: TevmConfig): Chain {
  if (config.chainId === 31337) return foundry;
  return {
    id: config.chainId,
    name:
      config.mode === "fork-ethereum"
        ? "Ethereum (Fork)"
        : config.mode === "fork-base"
          ? "Base (Fork)"
          : `Chain ${String(config.chainId)} (Fork)`,
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: { default: { http: [] } },
  };
}

/**
 * Create a Web Worker running Tevm and return viem clients connected to it.
 *
 * Initializes the worker, waits for it to be ready, then creates
 * PublicClient + WalletClient + TestClient backed by the worker transport.
 */
export async function createTevmClients(
  config: TevmConfig = { mode: "from-scratch", chainId: 31337 },
): Promise<TevmClients> {
  const worker = new Worker(new URL("./tevm-worker.ts", import.meta.url), {
    type: "module",
  });

  // Wait for worker to be ready
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Tevm Worker initialization timed out after 30s"));
    }, 30_000);

    const handler = (
      event: MessageEvent<WorkerReadyMessage | WorkerErrorMessage>,
    ) => {
      const data = event.data;
      if ("type" in data) {
        if (data.type === "ready") {
          clearTimeout(timeout);
          worker.removeEventListener("message", handler);
          resolve();
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (data.type === "error") {
          clearTimeout(timeout);
          worker.removeEventListener("message", handler);
          reject(new Error(`Tevm Worker failed to initialize: ${data.error}`));
        }
      }
    };

    worker.addEventListener("message", handler);

    const initMsg: WorkerInitMessage = { type: "init", config };
    worker.postMessage(initMsg);
  });

  const chain = buildChain(config);
  const transport = createTevmTransport(worker);

  const publicClient = createPublicClient({ chain, transport });

  // Use privateKeyToAccount so signing operations (signTypedData, signMessage)
  // happen locally — Tevm doesn't handle wallet RPC methods like eth_signTypedData_v4.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const deployer = PREFUNDED_ACCOUNTS[0]!;
  const walletClient = createWalletClient({
    chain,
    transport,
    account: privateKeyToAccount(deployer.privateKey),
  });

  const testClient = createTestClient({
    chain,
    transport,
    mode: "anvil",
  });

  return { publicClient, walletClient, testClient, worker, chain };
}
